import { ReactNode } from "react";
import { ModalStackItem } from "store/slices/modalStack";
import { ComposedModal } from "./ComposedModal";
import EmptyModal from "./EmptyModal";
import StackedEmptyModal from "./StackedEmptyModal";

type BaseModalProps = {
  buttonDisabled?: boolean;
  buttonText?: string;
  children?: ReactNode;
  fullWidth?: boolean; // For e.g. dividers that go all the way to the edge
  onButtonClicked?: () => void;
  onClose: () => void;
  subtitle?: ReactNode;
  title: string;
  contentClassName?: string;
  headerClassName?: string;
};

type ModalProps = BaseModalProps & {
  open?: boolean;
};

/**
 * @component BaseModal
 * @description Internal component containing the modal UI structure
 *
 * Design Reference: https://www.figma.com/design/X5eDWdGQXKwiobJgOJZiw5/Consensus-Design-Library?node-id=88-262
 */
function BaseModal({
  buttonDisabled,
  buttonText,
  children,
  fullWidth = false,
  onButtonClicked,
  onClose,
  subtitle,
  title,
  contentClassName = "",
  headerClassName = "",
}: BaseModalProps) {
  return (
    <ComposedModal>
      <ComposedModal.Header
        title={title}
        subtitle={subtitle}
        onClose={onClose}
        className={headerClassName}
        showBorder={!!children || !!onButtonClicked}
      />
      {children !== undefined && (
        <ComposedModal.Body fullWidth={fullWidth} className={contentClassName}>
          {children}
        </ComposedModal.Body>
      )}
      {!!onButtonClicked && (
        <ComposedModal.Footer
          primaryAction={{
            label: buttonText,
            onClick: onButtonClicked,
            disabled: buttonDisabled,
          }}
          secondaryAction={{
            onClick: onClose,
          }}
          showBorder={!!children}
        />
      )}
    </ComposedModal>
  );
}

/**
 * @component Modal
 * @description Renders a modal with standardized styling, with a title and subtitle, a body
 *   content section, and close and submit buttons. If onButtonClicked prop is not included,
 *   no buttons will be rendered.
 */
function Modal({ open, onClose, ...rest }: ModalProps) {
  return (
    <EmptyModal open={open} onClose={onClose}>
      <BaseModal onClose={onClose} {...rest} />
    </EmptyModal>
  );
}

type StackedModalProps = BaseModalProps & {
  modal: ModalStackItem;
};

/**
 * @component StackedModal
 * @description Renders a modal that integrates with the modal stack system.
 * Functions the same as Modal but automatically manages its open state via the modal stack.
 */
function StackedModal({ modal, onClose, ...rest }: StackedModalProps) {
  return (
    <StackedEmptyModal modal={modal} onClose={onClose}>
      <BaseModal onClose={onClose} {...rest} />
    </StackedEmptyModal>
  );
}

export { Modal, StackedModal };
export default Modal;
